// salaryService.js
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  Timestamp,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

// Salary types
export const SALARY_TYPES = {
  MONTHLY: "monthly",
  HOURLY: "hourly",
  PROJECT_BASED: "project_based",
  INTERN_STIPEND: "intern_stipend",
};

// Default salary/stipend amounts
export const DEFAULT_SALARY_AMOUNTS = {
  INDIVIDUAL_INTERN_STIPEND: 2000, // Monthly stipend for individual interns
};

// Payment methods
export const PAYMENT_METHODS = {
  CASH: "cash",
  BANK_TRANSFER: "bank_transfer",
  UPI: "upi",
  CHEQUE: "cheque",
  CARD: "card",
};

// Payment status
export const PAYMENT_STATUS = {
  PENDING: "pending",
  PAID: "paid",
  PARTIALLY_PAID: "partially_paid",
  OVERDUE: "overdue",
  CANCELLED: "cancelled",
};

// Salary components
export const SALARY_COMPONENTS = {
  BASE_SALARY: "base_salary",
  ALLOWANCES: "allowances",
  OVERTIME: "overtime",
  BONUS: "bonus",
  DEDUCTIONS: "deductions",
  TAX: "tax",
};

const SALARIES_COLLECTION = "salaries";
const SALARY_RECORDS_COLLECTION = "salary_records";
const SALARY_PAYMENTS_COLLECTION = "salary_payments";

// Add or update user salary configuration
export const addOrUpdateSalaryConfig = async (salaryConfig) => {
  try {
    // Check if salary config already exists for the user
    const q = query(
      collection(db, SALARIES_COLLECTION),
      where("userId", "==", salaryConfig.userId),
      where("isActive", "==", true)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // Update existing config
      const existingDoc = querySnapshot.docs[0];
      const salaryRef = doc(db, SALARIES_COLLECTION, existingDoc.id);

      await updateDoc(salaryRef, {
        ...salaryConfig,
        updatedAt: Timestamp.now(),
      });

      toast.success("Salary configuration updated successfully!");
      return { id: existingDoc.id, ...salaryConfig };
    } else {
      // Create new config
      const salary = {
        ...salaryConfig,
        isActive: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, SALARIES_COLLECTION), salary);

      toast.success("Salary configuration added successfully!");
      return { id: docRef.id, ...salary };
    }
  } catch (error) {
    console.error("Error adding/updating salary config:", error);
    toast.error("Failed to save salary configuration");
    throw error;
  }
};

// Get salary configuration by user ID
export const getSalaryConfigByUserId = async (userId) => {
  try {
    const q = query(
      collection(db, SALARIES_COLLECTION),
      where("userId", "==", userId),
      where("isActive", "==", true)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const data = querySnapshot.docs[0].data();
      return {
        id: querySnapshot.docs[0].id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching salary config:", error);
    return null;
  }
};

// Get all salary configurations
export const getAllSalaryConfigs = async () => {
  try {
    const q = query(
      collection(db, SALARIES_COLLECTION),
      where("isActive", "==", true),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const salaries = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      salaries.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      });
    });

    return salaries;
  } catch (error) {
    console.error("Error fetching salary configs:", error);
    toast.error("Failed to fetch salary configurations");
    return [];
  }
};

// Generate salary record for a user for a specific month
export const generateSalaryRecord = async (userId, userName, month, year, overrides = {}) => {
  try {
    // Check if record already exists
    const existingQ = query(
      collection(db, SALARY_RECORDS_COLLECTION),
      where("userId", "==", userId),
      where("month", "==", month),
      where("year", "==", year)
    );

    const existingSnapshot = await getDocs(existingQ);

    if (!existingSnapshot.empty) {
      throw new Error(`Salary record already exists for ${month}/${year}`);
    }

    // Get salary configuration
    const salaryConfig = await getSalaryConfigByUserId(userId);

    if (!salaryConfig) {
      throw new Error("No salary configuration found for this user");
    }

    // Calculate salary components
    const calculations = await calculateSalary(userId, salaryConfig, month, year);

    const salaryRecord = {
      userId,
      userName,
      month,
      year,
      salaryConfigId: salaryConfig.id,
      baseSalary: salaryConfig.baseSalary,
      salaryType: salaryConfig.salaryType,
      ...calculations,
      ...overrides,
      status: PAYMENT_STATUS.PENDING,
      generatedAt: Timestamp.now(),
      createdAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, SALARY_RECORDS_COLLECTION), salaryRecord);

    toast.success("Salary record generated successfully!");
    return { id: docRef.id, ...salaryRecord };
  } catch (error) {
    console.error("Error generating salary record:", error);
    toast.error(error.message || "Failed to generate salary record");
    throw error;
  }
};

// Calculate salary based on attendance and configuration
export const calculateSalary = async (userId, salaryConfig, month, year) => {
  try {
    // Import services here to avoid circular dependency
    const { getUserAttendanceForMonth } = await import("./attendanceService");
    const { getLeavesByUserId } = await import("./leaveService");

    // Get attendance data for the specific month (all records, not limited)
    const monthlyAttendance = await getUserAttendanceForMonth(userId, month, year);
    
    // Get approved leaves for the month
    const allLeaves = await getLeavesByUserId(userId);
    const approvedLeavesInMonth = allLeaves.filter(leave => {
      if (leave.status !== "approved") return false;
      
      const startDate = new Date(leave.startDate);
      const endDate = new Date(leave.endDate);
      
      return (
        (startDate.getMonth() + 1 === month && startDate.getFullYear() === year) ||
        (endDate.getMonth() + 1 === month && endDate.getFullYear() === year) ||
        (startDate <= new Date(year, month - 1, 1) && endDate >= new Date(year, month, 0))
      );
    });

    // Calculate leave days
    const { calculateLeaveDays } = await import("./leaveService");
    let totalLeaveDays = 0;
    approvedLeavesInMonth.forEach(leave => {
      const leaveDays = calculateLeaveDays(leave.startDate, leave.endDate);
      // Only count leaves that fall within this month
      const startDate = new Date(leave.startDate);
      const endDate = new Date(leave.endDate);
      const monthStart = new Date(year, month - 1, 1);
      const monthEnd = new Date(year, month, 0);
      
      if (startDate <= monthEnd && endDate >= monthStart) {
        totalLeaveDays += leaveDays;
      }
    });

    // Calculate working days (days with check-in) and hours
    const workingDays = monthlyAttendance.filter(record => record.checkIn && record.totalHours > 0).length;
    const totalHours = monthlyAttendance.reduce((sum, record) => sum + (record.totalHours || 0), 0);
    const presentDays = workingDays;
    const daysOnLeave = totalLeaveDays;

    let grossSalary = 0;
    let overtimeAmount = 0;
    let allowances = salaryConfig.allowances || 0;
    let deductions = salaryConfig.deductions || 0;

    if (salaryConfig.salaryType === SALARY_TYPES.MONTHLY) {
      grossSalary = salaryConfig.baseSalary;
      
      // Calculate overtime if applicable
      const standardHours = 8 * workingDays;
      if (totalHours > standardHours && salaryConfig.overtimeRate) {
        const overtimeHours = totalHours - standardHours;
        overtimeAmount = overtimeHours * salaryConfig.overtimeRate;
      }
    } else if (salaryConfig.salaryType === SALARY_TYPES.HOURLY) {
      grossSalary = totalHours * salaryConfig.baseSalary;
    }

    // Calculate tax (simple calculation - 10% if gross > 50000)
    const taxAmount = grossSalary > 50000 ? grossSalary * 0.1 : 0;

    const netSalary = grossSalary + allowances + overtimeAmount - deductions - taxAmount;

    return {
      presentDays,
      workingDays,
      daysOnLeave,
      totalHours: Math.round(totalHours * 100) / 100,
      grossSalary,
      allowances,
      overtimeAmount,
      deductions,
      taxAmount,
      netSalary,
    };
  } catch (error) {
    console.error("Error calculating salary:", error);
    throw error;
  }
};

// Get salary records by user ID
export const getSalaryRecordsByUserId = async (userId, limitCount = 12) => {
  try {
    const q = query(
      collection(db, SALARY_RECORDS_COLLECTION),
      where("userId", "==", userId),
      orderBy("year", "desc"),
      orderBy("month", "desc"),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const records = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      records.push({
        id: doc.id,
        ...data,
        generatedAt: data.generatedAt?.toDate(),
        createdAt: data.createdAt?.toDate(),
        paidAt: data.paidAt?.toDate(),
      });
    });

    return records;
  } catch (error) {
    console.error("Error fetching salary records:", error);
    return [];
  }
};

// Get all salary records with filters
export const getAllSalaryRecords = async (filters = {}) => {
  try {
    let q = collection(db, SALARY_RECORDS_COLLECTION);

    const conditions = [];

    if (filters.month) {
      conditions.push(where("month", "==", filters.month));
    }

    if (filters.year) {
      conditions.push(where("year", "==", filters.year));
    }

    if (filters.status) {
      conditions.push(where("status", "==", filters.status));
    }

    if (conditions.length > 0) {
      q = query(q, ...conditions, orderBy("createdAt", "desc"));
    } else {
      q = query(q, orderBy("createdAt", "desc"));
    }

    const querySnapshot = await getDocs(q);
    const records = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      records.push({
        id: doc.id,
        ...data,
        generatedAt: data.generatedAt?.toDate(),
        createdAt: data.createdAt?.toDate(),
        paidAt: data.paidAt?.toDate(),
      });
    });

    return records;
  } catch (error) {
    console.error("Error fetching salary records:", error);
    toast.error("Failed to fetch salary records");
    return [];
  }
};

// Update salary record status
export const updateSalaryRecordStatus = async (recordId, status, paidAmount = null) => {
  try {
    const recordRef = doc(db, SALARY_RECORDS_COLLECTION, recordId);

    const updateData = {
      status,
      updatedAt: Timestamp.now(),
    };

    if (status === PAYMENT_STATUS.PAID && paidAmount) {
      updateData.paidAmount = paidAmount;
      updateData.paidAt = Timestamp.now();
    }

    await updateDoc(recordRef, updateData);

    toast.success("Salary record updated successfully!");
    return true;
  } catch (error) {
    console.error("Error updating salary record:", error);
    toast.error("Failed to update salary record");
    throw error;
  }
};

// Record salary payment (detailed transaction tracking)
export const recordSalaryPayment = async (paymentData) => {
  try {
    const {
      recordId,
      userId,
      userName,
      amount,
      paymentMethod,
      paymentDate,
      referenceNumber,
      notes,
    } = paymentData;

    // Get the salary record
    const recordRef = doc(db, SALARY_RECORDS_COLLECTION, recordId);
    const recordSnap = await getDoc(recordRef);

    if (!recordSnap.exists()) {
      throw new Error("Salary record not found");
    }

    const salaryRecord = recordSnap.data();

    // Create payment transaction
    const payment = {
      recordId,
      userId,
      userName,
      amount: parseFloat(amount),
      paymentMethod,
      paymentDate: paymentDate ? Timestamp.fromDate(new Date(paymentDate)) : Timestamp.now(),
      referenceNumber: referenceNumber || "",
      notes: notes || "",
      month: salaryRecord.month,
      year: salaryRecord.year,
      netSalary: salaryRecord.netSalary,
      createdAt: Timestamp.now(),
      createdBy: "admin", // You can pass current user info here
    };

    // Add payment to salary_payments collection
    const paymentRef = await addDoc(collection(db, SALARY_PAYMENTS_COLLECTION), payment);

    // Get all payments for this record to calculate total paid
    const paymentsQuery = query(
      collection(db, SALARY_PAYMENTS_COLLECTION),
      where("recordId", "==", recordId)
    );
    const paymentsSnapshot = await getDocs(paymentsQuery);
    
    let totalPaid = 0;
    paymentsSnapshot.forEach((doc) => {
      totalPaid += doc.data().amount || 0;
    });

    // Update salary record status based on payment
    let newStatus = PAYMENT_STATUS.PENDING;
    const netSalary = salaryRecord.netSalary || 0;

    if (totalPaid >= netSalary) {
      newStatus = PAYMENT_STATUS.PAID;
    } else if (totalPaid > 0) {
      newStatus = PAYMENT_STATUS.PARTIALLY_PAID;
    }

    await updateDoc(recordRef, {
      status: newStatus,
      totalPaid,
      lastPaymentDate: payment.paymentDate,
      updatedAt: Timestamp.now(),
    });

    // Add to revenue expenses (similar to student fee revenue integration)
    try {
      const { addManualExpenseRecord } = await import("./revenueService");
      await addManualExpenseRecord({
        description: `Salary Payment - ${userName} (${getMonthName(salaryRecord.month)} ${salaryRecord.year})`,
        amount: parseFloat(amount),
        category: "Salaries",
        subcategory: "Employee Salary",
        paymentMethod: paymentMethod,
        referenceNumber: referenceNumber || "",
        date: paymentDate ? new Date(paymentDate) : new Date(),
        notes: notes || `Payment for ${getMonthName(salaryRecord.month)} ${salaryRecord.year}`,
      });
    } catch (revenueError) {
      console.warn("Failed to record revenue expense:", revenueError);
      // Don't throw error - payment is still recorded
    }

    // Log activity
    try {
      const { logActivity } = await import("./activityService");
      await logActivity({
        action: "salary_payment_recorded",
        description: `Recorded salary payment of ${formatCurrency(amount)} for ${userName}`,
        metadata: {
          recordId,
          userId,
          amount,
          paymentMethod,
          month: salaryRecord.month,
          year: salaryRecord.year,
          totalPaid,
          newStatus,
        },
      });
    } catch (activityError) {
      console.warn("Failed to log activity:", activityError);
    }

    toast.success(`Payment of ${formatCurrency(amount)} recorded successfully!`);
    return { id: paymentRef.id, ...payment };
  } catch (error) {
    console.error("Error recording salary payment:", error);
    toast.error(error.message || "Failed to record salary payment");
    throw error;
  }
};

// Get all payments for a salary record
export const getSalaryPayments = async (recordId) => {
  try {
    const q = query(
      collection(db, SALARY_PAYMENTS_COLLECTION),
      where("recordId", "==", recordId),
      orderBy("paymentDate", "desc")
    );

    const querySnapshot = await getDocs(q);
    const payments = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      payments.push({
        id: doc.id,
        ...data,
        paymentDate: data.paymentDate?.toDate(),
        createdAt: data.createdAt?.toDate(),
      });
    });

    return payments;
  } catch (error) {
    console.error("Error fetching salary payments:", error);
    return [];
  }
};

// Get all payments (for revenue integration)
export const getAllSalaryPayments = async (filters = {}) => {
  try {
    let q = collection(db, SALARY_PAYMENTS_COLLECTION);

    const conditions = [];

    if (filters.month) {
      conditions.push(where("month", "==", filters.month));
    }

    if (filters.year) {
      conditions.push(where("year", "==", filters.year));
    }

    if (filters.userId) {
      conditions.push(where("userId", "==", filters.userId));
    }

    if (conditions.length > 0) {
      q = query(q, ...conditions, orderBy("paymentDate", "desc"));
    } else {
      q = query(q, orderBy("paymentDate", "desc"));
    }

    const querySnapshot = await getDocs(q);
    const payments = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      payments.push({
        id: doc.id,
        ...data,
        paymentDate: data.paymentDate?.toDate(),
        createdAt: data.createdAt?.toDate(),
      });
    });

    return payments;
  } catch (error) {
    console.error("Error fetching all salary payments:", error);
    return [];
  }
};

// Delete salary configuration
export const deleteSalaryConfig = async (configId) => {
  try {
    const configRef = doc(db, SALARIES_COLLECTION, configId);
    
    await updateDoc(configRef, {
      isActive: false,
      deletedAt: Timestamp.now(),
    });

    toast.success("Salary configuration deleted successfully!");
    return true;
  } catch (error) {
    console.error("Error deleting salary config:", error);
    toast.error("Failed to delete salary configuration");
    throw error;
  }
};

// Get salary statistics
export const getSalaryStatistics = async (filters = {}) => {
  try {
    const records = await getAllSalaryRecords(filters);

    const stats = {
      totalRecords: records.length,
      totalGrossSalary: 0,
      totalNetSalary: 0,
      totalPaid: 0,
      totalPending: 0,
      averageGrossSalary: 0,
      averageNetSalary: 0,
      statusBreakdown: {
        [PAYMENT_STATUS.PENDING]: 0,
        [PAYMENT_STATUS.PAID]: 0,
        [PAYMENT_STATUS.PARTIALLY_PAID]: 0,
        [PAYMENT_STATUS.OVERDUE]: 0,
        [PAYMENT_STATUS.CANCELLED]: 0,
      },
      monthlyTrends: {},
    };

    records.forEach((record) => {
      stats.totalGrossSalary += record.grossSalary || 0;
      stats.totalNetSalary += record.netSalary || 0;

      if (record.status === PAYMENT_STATUS.PAID) {
        stats.totalPaid += record.paidAmount || record.netSalary || 0;
      } else {
        stats.totalPending += record.netSalary || 0;
      }

      // Status breakdown
      stats.statusBreakdown[record.status]++;

      // Monthly trends
      const monthKey = `${record.year}-${record.month.toString().padStart(2, '0')}`;
      if (!stats.monthlyTrends[monthKey]) {
        stats.monthlyTrends[monthKey] = {
          grossSalary: 0,
          netSalary: 0,
          count: 0,
        };
      }
      stats.monthlyTrends[monthKey].grossSalary += record.grossSalary || 0;
      stats.monthlyTrends[monthKey].netSalary += record.netSalary || 0;
      stats.monthlyTrends[monthKey].count++;
    });

    if (records.length > 0) {
      stats.averageGrossSalary = Math.round(stats.totalGrossSalary / records.length);
      stats.averageNetSalary = Math.round(stats.totalNetSalary / records.length);
    }

    return stats;
  } catch (error) {
    console.error("Error getting salary statistics:", error);
    return null;
  }
};

// Utility function to format currency
export const formatCurrency = (amount, currency = "INR") => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
  }).format(amount || 0);
};

// Utility function to get month name
export const getMonthName = (month) => {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return months[month - 1] || "Unknown";
};

// Generate bulk salary records for all active users (or specific roles)
export const generateBulkSalaryRecords = async (month, year, targetRoles = ['tutor', 'employee', 'individual_intern']) => {
  try {
    const { getStudents, getTutors } = await import("./userService");

    let allUsers = [];

    // Fetch users based on target roles
    if (targetRoles.includes('tutor')) {
      const tutors = await getTutors();
      allUsers = [...allUsers, ...tutors.filter(user => user.isActive).map(user => ({...user, role: 'tutor'}))];
    }

    if (targetRoles.includes('employee')) {
      const employees = await getStudents();
      const employeeUsers = employees.filter(user => 
        user.isActive && user.studentLevel === 'employee'
      ).map(user => ({...user, role: 'employee'}));
      allUsers = [...allUsers, ...employeeUsers];
    }

    // Add individual interns (not assigned to colleges)
    if (targetRoles.includes('individual_intern')) {
      const students = await getStudents();
      const individualInterns = students.filter(user => 
        user.isActive && 
        user.studentLevel === 'intern' && 
        !user.collegeId // Only individual interns, not college interns
      ).map(user => ({...user, role: 'individual_intern'}));
      allUsers = [...allUsers, ...individualInterns];
    }

    const results = {
      success: [],
      errors: [],
    };

    for (const user of allUsers) {
      try {
        await generateSalaryRecord(user.id, user.name, month, year);
        results.success.push(`${user.name} (${user.role})`);
      } catch (error) {
        results.errors.push({
          name: `${user.name} (${user.role})`,
          error: error.message,
        });
      }
    }

    if (results.success.length > 0) {
      toast.success(`Generated salary records for ${results.success.length} users`);
    }

    if (results.errors.length > 0) {
      console.warn("Some salary records failed to generate:", results.errors);
    }

    return results;
  } catch (error) {
    console.error("Error generating bulk salary records:", error);
    toast.error("Failed to generate bulk salary records");
    throw error;
  }
};

// Get users eligible for salary configuration (tutors, employees, individual interns)
export const getEligibleUsersForSalary = async () => {
  try {
    const { getStudents, getTutors } = await import("./userService");
    
    let eligibleUsers = [];

    // Get tutors
    try {
      const tutors = await getTutors();
      const activeTutors = tutors.filter(user => user.isActive).map(user => ({
        ...user,
        role: 'tutor',
        displayRole: 'Tutor'
      }));
      eligibleUsers = [...eligibleUsers, ...activeTutors];
    } catch (error) {
      console.warn("Could not fetch tutors:", error);
    }

    // Get employees and individual interns from students
    try {
      const students = await getStudents();
      
      // Add employees
      const employees = students.filter(user => 
        user.isActive && user.studentLevel === 'employee'
      ).map(user => ({
        ...user,
        role: 'employee',
        displayRole: 'Employee'
      }));
      
      // Add individual interns (not assigned to colleges)
      const individualInterns = students.filter(user => 
        user.isActive && 
        user.studentLevel === 'intern' && 
        !user.collegeId // Only individual interns
      ).map(user => ({
        ...user,
        role: 'individual_intern',
        displayRole: 'Individual Intern'
      }));

      eligibleUsers = [...eligibleUsers, ...employees, ...individualInterns];
    } catch (error) {
      console.warn("Could not fetch students:", error);
    }

    return eligibleUsers;
  } catch (error) {
    console.error("Error fetching eligible users for salary:", error);
    return [];
  }
};